import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthenticatedUser } from '@/types/api';

// Lazy load getCurrentUser to avoid circular imports
const getAuthApi = async () => {
  const { getCurrentUser } = await import('@/lib/api/auth');
  return { getCurrentUser };
};

interface AuthState {
  // Backend user data from /auth/me (Clerk session token identifies the caller)
  currentUser: AuthenticatedUser | null;
  isLoadingCurrentUser: boolean;
  error: string | null;
  // Actions
  setCurrentUser: (user: AuthenticatedUser | null) => void;
  setError: (error: string | null) => void;
  fetchCurrentUser: () => Promise<void>;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      currentUser: null,
      isLoadingCurrentUser: false,
      error: null,

      setCurrentUser: (user) => set({ currentUser: user }),
      setError: (error) => set({ error }),

      fetchCurrentUser: async () => {
        set({ isLoadingCurrentUser: true, error: null });
        try {
          const { getCurrentUser } = await getAuthApi();
          const user = await getCurrentUser();
          set({
            currentUser: user,
            isLoadingCurrentUser: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch current user',
            currentUser: null,
            isLoadingCurrentUser: false,
          });
        }
      },

      clearAuth: () =>
        set({
          currentUser: null,
          error: null,
          isLoadingCurrentUser: false,
        }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        currentUser: state.currentUser,
      }),
    },
  ),
);
